// src/components/DriverDash.js

import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "../styles/driverDash.css";

// Default vehicles list with vehicle numbers
const DEFAULT_VEHICLES = [
  "KV-4344"
];

function DriverDash() {
  const [user, setUser] = useState(null);
  const [userVehicles, setUserVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [meterReading, setMeterReading] = useState("");
  const [destinations, setDestinations] = useState([]);
  const [currentDestination, setCurrentDestination] = useState("");
  const [activeTrip, setActiveTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Use onAuthStateChanged to wait for auth state to be determined
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("Auth state changed:", currentUser ? "User logged in" : "No user");
      setAuthChecked(true);
      
      if (!currentUser) {
        console.log("No user found, redirecting to login");
        navigate("/login", { replace: true });
        return;
      }
      
      console.log("User authenticated:", currentUser.uid);

      try {
        // Fetch user data
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(userData);
          
          // Use user's vehicles if they exist, otherwise use default vehicles
          const vehicles = userData.vehicles && userData.vehicles.length > 0 
            ? userData.vehicles 
            : DEFAULT_VEHICLES;
          
          setUserVehicles(vehicles);
          console.log("Vehicles loaded:", vehicles);

          // Check for active trip
          const tripsQuery = query(
            collection(db, "drivers"),
            where("userId", "==", currentUser.uid),
            where("endTime", "==", null),
            orderBy("startTime", "desc"),
            limit(1)
          );
          const tripsSnapshot = await getDocs(tripsQuery);

          if (!tripsSnapshot.empty) {
            const tripData = {
              id: tripsSnapshot.docs[0].id,
              ...tripsSnapshot.docs[0].data(),
            };
            setActiveTrip(tripData);
            setSelectedVehicle(tripData.vehicle);
            setDestinations(tripData.destinations || []);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        alert("Error loading user data");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleStartTrip = async () => {
    if (!selectedVehicle) {
      alert("Please select a vehicle");
      return;
    }

    if (!meterReading || isNaN(meterReading)) {
      alert("Please enter a valid meter reading (numbers only)");
      return;
    }

    try {
      const tripData = {
        userId: auth.currentUser.uid,
        userName: user.name,
        vehicle: selectedVehicle,
        startMeterReading: parseFloat(meterReading),
        startTime: new Date().toISOString(),
        destinations: [],
        endTime: null,
        endMeterReading: null,
      };

      const docRef = await addDoc(collection(db, "drivers"), tripData);
      setActiveTrip({ id: docRef.id, ...tripData });
      setMeterReading("");
      alert("Trip started successfully!");
    } catch (error) {
      console.error("Error starting trip:", error);
      alert("Error starting trip");
    }
  };

  const handleAddDestination = () => {
    if (!currentDestination.trim()) {
      alert("Please enter a destination");
      return;
    }

    if (!activeTrip) {
      alert("Please start a trip first");
      return;
    }

    const newDestination = {
      location: currentDestination.trim(),
      time: new Date().toISOString(),
    };

    setDestinations([...destinations, newDestination]);
    setCurrentDestination("");
  };

  const handleSaveDestinations = async () => {
    if (!activeTrip) {
      alert("No active trip");
      return;
    }

    try {
      await updateDoc(doc(db, "drivers", activeTrip.id), {
        destinations: destinations,
      });
      alert("Destinations saved successfully!");
    } catch (error) {
      console.error("Error saving destinations:", error);
      alert("Error saving destinations");
    }
  };

  const handleEndTrip = async () => {
    if (!activeTrip) {
      alert("No active trip to end");
      return;
    }

    if (!meterReading || isNaN(meterReading)) {
      alert("Please enter a valid end meter reading (numbers only)");
      return;
    }

    const endMeterValue = parseFloat(meterReading);
    const startMeterValue = activeTrip.startMeterReading;

    // Validate end meter reading is greater than start meter reading
    if (endMeterValue <= startMeterValue) {
      alert(`End meter reading (${endMeterValue}) must be greater than start meter reading (${startMeterValue})`);
      return;
    }

    // Validate at least one destination has been saved
    if (!destinations || destinations.length === 0) {
      alert("Please add and save at least one destination before ending the trip");
      return;
    }

    try {
      await updateDoc(doc(db, "drivers", activeTrip.id), {
        endTime: new Date().toISOString(),
        endMeterReading: endMeterValue,
        destinations: destinations,
      });

      alert("Trip ended successfully!");
      setActiveTrip(null);
      setSelectedVehicle("");
      setMeterReading("");
      setDestinations([]);
    } catch (error) {
      console.error("Error ending trip:", error);
      alert("Error ending trip");
    }
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to logout?")) {
      try {
        // Sign out from Firebase
        await signOut(auth);
        
        // Clear localStorage
        localStorage.removeItem("authToken");
        localStorage.removeItem("role");
        localStorage.removeItem("userId");
        // Clear any other items you might have stored
        
        // Navigate to login page
        navigate("/", { replace: true });
      } catch (error) {
        console.error("Error logging out:", error);
        alert("Error logging out: " + error.message);
      }
    }
  };

  // Show loading only if auth hasn't been checked yet
  if (!authChecked || loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="driver-dash-container">
      <div className="driver-dash-header">
        <h1>Welcome, {user?.name}!</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="logout-btn" onClick={() => navigate("/superadmin")} style={{ backgroundColor: '#4CAF50' }}>
            Back to Admin
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="driver-dash-content">
        {activeTrip && (
          <div className="active-trip-alert">
            <strong>Active Trip in Progress</strong>
            <p>Started: {new Date(activeTrip.startTime).toLocaleString()}</p>
            <p>Start Meter: {activeTrip.startMeterReading}</p>
          </div>
        )}

        <div className="trip-section">
          <h2>{activeTrip ? "Current Trip" : "Start New Trip"}</h2>

          <div className="form-group">
            <label>Select Vehicle Number:</label>
            <select
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              disabled={activeTrip !== null}
              className="vehicle-select"
            >
              <option value="">-- Select Vehicle Number --</option>
              {userVehicles.map((vehicle, index) => (
                <option key={index} value={vehicle}>
                  {vehicle}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              {activeTrip ? "End Meter Reading:" : "Start Meter Reading:"}
            </label>
            <input
              type="number"
              value={meterReading}
              onChange={(e) => setMeterReading(e.target.value)}
              placeholder="Enter meter reading"
              className="meter-input"
            />
          </div>

          {!activeTrip ? (
            <button className="start-trip-btn" onClick={handleStartTrip}>
              Start Trip
            </button>
          ) : (
            <button className="end-trip-btn" onClick={handleEndTrip}>
              End Trip
            </button>
          )}
        </div>

        {activeTrip && (
          <div className="destination-section">
            <h2>Destinations</h2>

            <div className="destination-input-group">
              <input
                type="text"
                value={currentDestination}
                onChange={(e) => setCurrentDestination(e.target.value)}
                placeholder="Enter destination"
                className="destination-input"
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleAddDestination();
                }}
              />
              <button className="add-dest-btn" onClick={handleAddDestination}>
                Add Destination
              </button>
            </div>

            {destinations.length > 0 && (
              <>
                <div className="destinations-list">
                  <h3>Added Destinations:</h3>
                  <ul>
                    {destinations.map((dest, index) => (
                      <li key={index}>
                        <span className="dest-number">{index + 1}.</span>
                        <span className="dest-location">{dest.location}</span>
                        <span className="dest-time">
                          {new Date(dest.time).toLocaleTimeString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button className="save-dest-btn" onClick={handleSaveDestinations}>
                  Save Destinations
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DriverDash;